import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import type { ErrorBoundaryProps, FallbackRender } from "@sentry/react/types/errorboundary";
import type { Scope } from "@sentry/types";
import type { ReactNode, FC } from "react";
import { useEffect, useState } from "react";

import type { To } from "@shared/react-router-dom";
import { useLocation } from "@shared/react-router-dom";
import { useInitialProps } from "@unauthenticated/catalog/context";

import { Fallback } from "./Fallback";

interface Props {
  to?: To;
  errorComponent?: ReactNode;
  children: ReactNode;
}

let globalResetError;

const ErrorBoundary: FC<Props> = ({ children, ...rest }) => {
  const { pathname } = useLocation();
  const { fullscriptCountry } = useInitialProps();
  const [isErrorThrown, setIsErrorThrown] = useState(false);

  useEffect(() => {
    if (isErrorThrown) {
      setIsErrorThrown(false);
      globalResetError?.();
    }
  }, [pathname]);

  const renderFallback: FallbackRender = ({ resetError }) => {
    globalResetError = resetError;

    return <Fallback {...rest} />;
  };

  const handleCapture: ErrorBoundaryProps["beforeCapture"] = (scope: Scope) => {
    scope.setTags({ spa: "openCatalog", country: fullscriptCountry });
  };

  return (
    <SentryErrorBoundary
      showDialog={false}
      fallback={renderFallback}
      beforeCapture={handleCapture}
      onError={() => setIsErrorThrown(true)}
    >
      {children}
    </SentryErrorBoundary>
  );
};

export { ErrorBoundary };
